/** Backend serializer'lariga mos turlar: apps/carriers/serializers.py */
import type { ApplicationBrief, ApplicationStatus, DriverListItem, FreightType } from "@/features/drivers/types";

export type CarrierStatus = "active" | "paused" | "stopped";
export type JobStatus = "open" | "filled" | "closed";

export const CARRIER_STATUS_LABEL: Record<CarrierStatus, string> = { active: "Active", paused: "Paused", stopped: "Stopped" };
export const JOB_STATUS_LABEL: Record<JobStatus, string> = { open: "Open", filled: "Filled", closed: "Closed" };
export const APPLICATION_STATUS_LABEL: Record<ApplicationStatus, string> = {
  submitted: "Submitted",
  background_check: "Background check",
  orientation: "Orientation",
  hired: "Hired",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
};

export interface Carrier {
  id: number;
  name: string;
  mc_number: string;
  dot_number: string;
  status: CarrierStatus;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  city: string;
  state: string;
  hiring_states: string[];
  submission_method: string;
  reply_time: string;
  /** Har ishga olingan driver uchun carrier to'laydigan summa (joylashtirishga standart) */
  placement_fee: string | null;
  notes: string;
  open_jobs: number;
  submitted_count: number;
  hired_count: number;
  rejected_count: number;
  rejection_rate: number;
  /** Truck'lar (faol): jami, bo'sh (driver kerak), sug'urta/inspeksiya muddati yaqin yoki o'tgan */
  trucks_total: number;
  trucks_available: number;
  trucks_expiring: number;
  is_archived: boolean;
  created_at: string;
  version: number;
}

export interface CarrierOption {
  id: number;
  name: string;
  status: CarrierStatus;
}

export interface CarrierWrite {
  name: string;
  mc_number?: string;
  dot_number?: string;
  status?: CarrierStatus;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  city?: string;
  state?: string;
  hiring_states?: string[];
  submission_method?: string;
  reply_time?: string;
  placement_fee?: string | null;
  notes?: string;
}

/** Carrier qanday driverlarni oladi (bir nechtasi) */
export const HIRING_TYPES = ["company", "lease", "owner", "team"] as const;
export type HiringType = (typeof HIRING_TYPES)[number];

export interface JobRequirements {
  mvr?: string;
  dui?: string;
  drug_test?: string;
}

export interface Job {
  id: number;
  carrier: number;
  carrier_name: string;
  carrier_status: CarrierStatus;
  title: string;
  status: JobStatus;
  states: string[];
  min_experience_months: number;
  cdl_class: string;
  endorsements_required: string[];
  pay_text: string;
  pay_min_weekly: string | null;
  home_time: string;
  equipment: string;
  driving_type: string;
  driver_types: HiringType[];
  freight_type: FreightType | "";
  sign_on_bonus: string;
  requirements: JobRequirements;
  openings: number;
  posted_at: string | null;
  notes: string;
  candidates_count: number;
  hired_count: number;
  /** Carrierning bo'sh truck'lari (driver kerak) */
  carrier_trucks_available: number;
  submitted_count: number;
  is_archived: boolean;
  created_at: string;
  version: number;
}

export interface JobWrite {
  carrier: number;
  title: string;
  status?: JobStatus;
  states?: string[];
  min_experience_months?: number;
  cdl_class?: string;
  endorsements_required?: string[];
  pay_text?: string;
  home_time?: string;
  equipment?: string;
  driving_type?: string;
  driver_types?: HiringType[];
  freight_type?: FreightType | "";
  sign_on_bonus?: string;
  requirements?: JobRequirements;
  openings?: number;
  posted_at?: string | null;
  notes?: string;
}

/** /api/carriers/:id/candidates/ qatori */
export interface CarrierCandidate extends ApplicationBrief {
  driver: { id: number; full_name: string; owner: string | null };
}

/** /api/jobs/:id/candidates/ qatori: driver to'liq ro'yxat ko'rinishida */
export interface JobCandidate extends ApplicationBrief {
  driver: DriverListItem;
}

export interface JobCandidates {
  results: JobCandidate[];
  hidden_count: number;
  by_status: Partial<Record<ApplicationStatus, number>>;
}

export interface EligibleDriver extends DriverListItem {
  warnings: string[];
}

export interface RejectionReason {
  reason: string;
  count: number;
}

export interface SubmitResult {
  driver: DriverListItem;
  warnings: string[];
}

/** ["ALL"] -> "48 states", [] -> "—", aks holda "TX, OK" */
export function statesLabel(states: string[] | null | undefined): string {
  if (!states || states.length === 0) return "—";
  if (states.includes("ALL")) return "48 states";
  return states.join(", ");
}

export function experienceLabel(months: number): string {
  if (!months) return "No minimum";
  if (months < 12) return `${months}+ mo`;
  const years = months / 12;
  return `${Number.isInteger(years) ? years : years.toFixed(1)}+ yr`;
}

// ---- Truck'lar (backend: carriers.Truck, TruckSerializer) ----

export type TruckStatus = "available" | "assigned" | "in_shop" | "inactive";
export type CabType = "sleeper" | "day_cab";
export type Transmission = "automatic" | "manual";
export type TrailerType = "dry_van" | "reefer" | "flatbed" | "step_deck" | "conestoga" | "tanker" | "power_only" | "box_truck" | "car_hauler" | "other";
export type TruckCrew = "solo" | "team";
/** Sug'urta / inspeksiya muddati: o'tgan, yaqin (sozlamadagi kunlar ichida), joyida */
export type ExpiryState = "expired" | "soon" | "ok";

export const TRUCK_STATUSES: TruckStatus[] = ["available", "assigned", "in_shop", "inactive"];
export const CAB_TYPES: CabType[] = ["sleeper", "day_cab"];
export const TRANSMISSIONS: Transmission[] = ["automatic", "manual"];
export const TRAILER_TYPES: TrailerType[] = ["dry_van", "reefer", "flatbed", "step_deck", "conestoga", "tanker", "power_only", "box_truck", "car_hauler", "other"];

export interface TruckDriverRef {
  id: number;
  full_name: string;
  /** Profil o'z doirasida bo'lsa ochiladi (boshqa filial driveri -- faqat ismi) */
  can_open: boolean;
}

export interface Truck {
  id: number;
  carrier: number;
  carrier_name: string;
  unit_number: string;
  make: string;
  model: string;
  year: number | null;
  cab_type: CabType | "";
  transmission: Transmission | "";
  status: TruckStatus;
  trailer_type: TrailerType | "";
  trailer_length: number | null;
  vin: string;
  plate_number: string;
  plate_state: string;
  insurance_expires: string | null;
  inspection_expires: string | null;
  insurance_state: ExpiryState | null;
  inspection_state: ExpiryState | null;
  city: string;
  state: string;
  crew: TruckCrew;
  driver_ref: TruckDriverRef | null;
  co_driver_ref: TruckDriverRef | null;
  notes: string;
  is_archived: boolean;
  created_at: string;
  version: number;
}

export interface TruckWrite {
  carrier?: number;
  unit_number?: string;
  make?: string;
  model?: string;
  year?: number | null;
  cab_type?: CabType | "";
  transmission?: Transmission | "";
  status?: TruckStatus;
  trailer_type?: TrailerType | "";
  trailer_length?: number | null;
  vin?: string;
  plate_number?: string;
  plate_state?: string;
  insurance_expires?: string | null;
  inspection_expires?: string | null;
  city?: string;
  state?: string;
  crew?: TruckCrew;
  driver?: number | null;
  co_driver?: number | null;
  notes?: string;
  version?: number;
}
